/// <reference types="@types/googlemaps" />
import { Subject } from 'rxjs';

import { SearchContext, SearchResult } from '../../+state/service-locator/service-locator.models';

export class InfoWindow {
  private infoWindow_: google.maps.InfoWindow;
  private map_: google.maps.Map;
  private selectedShop$ = new Subject<boolean>();

  constructor(map: google.maps.Map, opts: google.maps.InfoWindowOptions) {
    this.map_ = map;
    this.infoWindow_ = new google.maps.InfoWindow(opts);
  }

  getSelectedShop$() {
    return this.selectedShop$;
  }

  private setSelectedShop(isSelected: boolean) {
    this.selectedShop$.next(isSelected);
  }

  openInfoWinfowForMarker(
    marker: google.maps.Marker,
    shop: SearchResult,
    searchContext: SearchContext
  ) {
    this.infoWindow_.setContent(this.setInfoWindowContent(shop, searchContext));

    const _self = this;
    google.maps.event.addListener(this.infoWindow_, 'domready', function () {
      // Shop Details applies to all Search Contexts
      const shopDetailsContent = document.getElementById('shopDetailsContent');
      const shopSelectionButton = document.getElementById('shopSelectionButton');
      const shopConfirmationButton = document.getElementById('shopConfirmationButton');
      const shopConfirmationContent = document.getElementById('shopConfirmationContent');
      const container = document.getElementById('shopInfoWindow');

      function toggleButton() {
        shopSelectionButton.style.display = 'none';
        // Remove shop selection button from screen reader processing
        shopSelectionButton.setAttribute('aria-hidden', 'true');

        // Focus confirmation button
        shopConfirmationButton.focus();

        // Hide details and remove from screen reader processing
        shopDetailsContent.style.display = 'none';
        shopDetailsContent.setAttribute('aria-hidden', 'true');

        shopConfirmationContent.style.display = 'block';
        shopConfirmationButton.style.display = 'block';

        // Turn off aria-live updates
        container.setAttribute('aria-live', 'off');
      }

      function selectShop() {
        _self.setSelectedShop(true);
      }
      // Attach listeners for DRP Shop Selection
      if (
        searchContext === SearchContext.DRP_SHOP_SELECTION ||
        searchContext === SearchContext.RENTAL_SELECTION
      ) {
        // Listen for click event on shop selection button and fire toggle on event
        shopSelectionButton.addEventListener('click', function () {
          toggleButton();
        });

        // Listen for enter keypress event on shop selection button and fire toggle on event
        shopSelectionButton.addEventListener('keypress', function (e) {
          if (e.key === 'Enter') {
            toggleButton();
          }
        });

        // Listen for enter keypress event on shop confirmation button and fire shop select on event
        shopConfirmationButton.addEventListener('click', function () {
          selectShop();
        });

        // Listen for enter keypress event on shop confirmation button and fire shop select on event
        shopConfirmationButton.addEventListener('keypress', function (e) {
          if (e.key === 'Enter') {
            selectShop();
          }
        });
      }
    });

    this.infoWindow_.open(this.map_, marker);

    // Add an on-close listener so we can clear the selected shop
    google.maps.event.addListener(this.infoWindow_, 'closeclick', function () {
      _self.setSelectedShop(false);
    });
  }

  private getShopDetailsElement(shop: SearchResult): HTMLDivElement {
    const shopDetailsContent = document.createElement('div');
    shopDetailsContent.id = 'shopDetailsContent';

    // -------- Address Row
    const addressLine1 = document.createElement('div');
    addressLine1.className = 'pad-bottom-5';
    addressLine1.innerHTML = `${shop.address.addressLine1}`;
    shopDetailsContent.appendChild(addressLine1);

    const addressLine2 = document.createElement('div');
    addressLine2.className = 'pad-bottom-5';
    addressLine2.innerHTML = `${shop.address.city}, ${shop.address.state} ${shop.address.zipCode}`;
    shopDetailsContent.appendChild(addressLine2);

    // -------- Phone row
    const phone = document.createElement('div');
    phone.className = 'pad-bottom-5';

    const phoneIcon = document.createElement('span');
    phoneIcon.className = 'icon-call color-blue pad-right-5';
    phoneIcon.setAttribute('tabindex', '0');
    phoneIcon.setAttribute('aria-label', 'Phone Number');

    const phoneNumber = document.createElement('a');
    phoneNumber.href = `tel:+1${shop.contactMethodDetail.primaryPhoneNumber}`;
    phoneNumber.innerHTML = `${shop.contactMethodDetail.primaryPhoneNumber}`;

    phone.appendChild(phoneIcon);
    phone.appendChild(phoneNumber);

    shopDetailsContent.appendChild(phone);

    // ----------- Website Row
    if (shop.contactMethodDetail.websiteAddresses[0]) {
      const website = document.createElement('div');
      website.className = 'pad-bottom-10';

      const websiteLink = document.createElement('a');
      websiteLink.href = `https://${shop.contactMethodDetail.websiteAddresses}`;
      websiteLink.target = '_blank';

      const websiteIcon = document.createElement('span');
      websiteIcon.className = 'icon-new-page color-blue pad-right-5';

      const websiteLinkText = document.createElement('span');
      websiteLinkText.innerHTML = 'Visit Website';
      websiteLinkText.setAttribute('aria-label', 'Visit Website in a new window');

      websiteLink.appendChild(websiteLinkText);

      website.appendChild(websiteIcon);
      website.appendChild(websiteLink);
      shopDetailsContent.appendChild(website);
    }

    return shopDetailsContent;
  }

  private getShopSelectionButton(): HTMLButtonElement {
    const selectButton = document.createElement('button');
    selectButton.type = 'button';
    selectButton.style.display = 'block';
    selectButton.innerHTML = 'Select this shop';
    selectButton.id = 'shopSelectionButton';
    selectButton.setAttribute('ds-button', '');
    selectButton.setAttribute('aria-label', 'Select this shop');

    return selectButton;
  }

  private getShopConfirmationButton(): HTMLButtonElement {
    const confirmButton = document.createElement('button');
    confirmButton.type = 'button';
    confirmButton.style.display = 'none';
    confirmButton.innerHTML = 'Yes, Select this shop';
    confirmButton.id = 'shopConfirmationButton';
    confirmButton.setAttribute('ds-button', '');
    confirmButton.setAttribute('aria-label', 'Yes, Select this shop');

    return confirmButton;
  }

  private getConfirmationElement(): HTMLDivElement {
    const confirmationContent = document.createElement('div');
    confirmationContent.id = 'shopConfirmationContent';
    confirmationContent.style.display = 'none';

    const confirmText = document.createElement('p');
    confirmText.innerText = 'Are you sure you want to select this shop?';
    confirmText.style.marginBottom = '10px';
    confirmationContent.appendChild(confirmText);
    return confirmationContent;
  }

  private setInfoWindowContent(shop: SearchResult, searchContext: SearchContext) {
    const container = document.createElement('div');
    container.id = 'shopInfoWindow';
    container.setAttribute('aria-live', 'polite');

    const h3 = document.createElement('h3');
    h3.innerHTML = `${shop.name}`;

    const body = document.createElement('div');
    body.style.display = 'flex';
    body.style['flex-direction'] = 'column';
    body.style['align-items'] = 'left';

    // Add the shop details element
    body.appendChild(this.getShopDetailsElement(shop));

    if (
      searchContext === SearchContext.DRP_SHOP_SELECTION ||
      searchContext === SearchContext.RENTAL_SELECTION
    ) {
      body.appendChild(this.getShopSelectionButton());
      body.appendChild(this.getConfirmationElement());
      body.appendChild(this.getShopConfirmationButton());
    }

    // Build main container
    container.appendChild(h3);
    container.appendChild(body);

    return container;
  }
}
